import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
    setKeyPaymentModel,
    setNextEnabled,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import paymentModelImage from "../../../../tenants/kolula-key/assets/img/subscription_model.png";
import singleBookingModelImage from "../../../../tenants/kolula-key/assets/img/single_booking_model.png";
import { useQuery } from "@tanstack/react-query";
import { fetchLocationEquipment } from "../../../../api/locations/locationEquipmentClient";
import type { EquipmentListProps } from "../../../../types/baseTypes";
import { useGetCommercialModelsByLocationQuery } from "../../../../api/commercialModels/commercialModelsApiSlice";

type PaymentModel = "subscription-new" | "subscription-existing" | "single-booking";

export type PaymentModelData = {
    id: PaymentModel;
    titleKey: string;
    descriptionKey: string;
    image: string;
    priceScale: Array<{ durationInMinutes: number; price: number }>;
    /** Formatted lowest price (e.g. "9.99") or null if no price to display */
    lowestPrice: string | null;
    /** Translation key for the price unit (e.g. "book_location.day" or "book_location.month") */
    priceUnitKey: string | null;
};

type KeyHandoverSubscriptionContextValue = {
    models: PaymentModelData[];
    selected: PaymentModelData | null;
    isLoading: boolean;
    select: (model: PaymentModelData) => void;
    deselect: () => void;
};

const KeyHandoverSubscriptionContext = createContext<
    KeyHandoverSubscriptionContextValue | undefined
>(undefined);

export function KeyHandoverSubscriptionProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();

    const selectedModelType = useSelector(
        (state: RootState) => state.bookingCreation.keyHandoverSelectedPaymentModel
    );

    const selectedLocationId = useSelector(
        (state: RootState) => state.bookingCreation.selectedLocation?.id
    );

    // Fetch commercial models (subscription plans) for the selected location
    const { data: subscriptionPlans, isLoading: isLoadingPlans } = useGetCommercialModelsByLocationQuery(
        selectedLocationId || "",
        {
            skip: !selectedLocationId,
        }
    );

    // Fetch services for the selected location to get pricing
    const {
        data: services,
        isLoading: isLoadingServices,
    } = useQuery<EquipmentListProps[]>({
        queryKey: ["location-equipment", selectedLocationId, "keys"],
        queryFn: () => fetchLocationEquipment(selectedLocationId as string, "keys"),
        enabled: !!selectedLocationId,
        staleTime: 1000 * 60, // 1 minute
    });

    // Find the lowest price across all services for single-booking model
    const singleBookingPriceScale = useMemo(() => {
        if (!services || services.length === 0) return [];

        // Collect all price scales from all services
        const allPriceScales = services
            .map(service => service?.priceCategoryInfo?.priceScale || [])
            .flat();

        if (allPriceScales.length === 0) return [];

        // Group by duration and find minimum price for each duration
        const priceByDuration: Record<number, number> = {};
        
        allPriceScales.forEach(pricePoint => {
            const duration = pricePoint.durationInMinutes;
            const price = pricePoint.price;
            
            if (!priceByDuration[duration] || price < priceByDuration[duration]) {
                priceByDuration[duration] = price;
            }
        });

        // Convert back to priceScale format
        return Object.entries(priceByDuration).map(([duration, price]) => ({
            durationInMinutes: Number(duration),
            price: price,
        }));
    }, [services]);

    // Get monthly subscription price from subscription plans
    const monthlySubscriptionPrice = useMemo(() => {
        if (!subscriptionPlans) return 0;
        const monthlyPlan = subscriptionPlans.find((m) => m.recurrence === 'monthly');
        return monthlyPlan?.price || 0;
    }, [subscriptionPlans]);

    // Compute lowest single-booking price for display
    const singleBookingLowestPrice = useMemo(() => {
        if (singleBookingPriceScale.length === 0) return null;
        const sorted = [...singleBookingPriceScale].sort((a, b) => a.price - b.price);
        return sorted[0].price;
    }, [singleBookingPriceScale]);

    const formatPrice = useCallback((price: number) => {
        return new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price);
    }, []);

    const models: PaymentModelData[] = useMemo(
        () => [
            {
                id: "single-booking",
                titleKey: "book_location.keys.subscription_model.single_booking_title",
                descriptionKey: "book_location.keys.subscription_model.single_booking_desc",
                image: singleBookingModelImage,
                priceScale: singleBookingPriceScale,
                lowestPrice: singleBookingLowestPrice !== null ? formatPrice(singleBookingLowestPrice) : null,
                priceUnitKey: "book_location.day",
            },
            {
                id: "subscription-existing",
                titleKey: "book_location.keys.subscription_model.subscription_existing_title",
                descriptionKey: "book_location.keys.subscription_model.subscription_existing_desc",
                image: paymentModelImage,
                priceScale: [],
                lowestPrice: null,
                priceUnitKey: null,
            },
            {
                id: "subscription-new",
                titleKey: "book_location.keys.subscription_model.subscription_new_title",
                descriptionKey: "book_location.keys.subscription_model.subscription_new_desc",
                image: paymentModelImage,
                priceScale: monthlySubscriptionPrice > 0 ? [
                    { durationInMinutes: 43200, price: monthlySubscriptionPrice }
                ] : [],
                lowestPrice: monthlySubscriptionPrice > 0 ? formatPrice(monthlySubscriptionPrice) : null,
                priceUnitKey: "book_location.month",
            },
        ],
        [monthlySubscriptionPrice, singleBookingPriceScale, singleBookingLowestPrice, formatPrice]
    );

    const selected = useMemo(() => {
        return models.find((m) => m.id === selectedModelType) || null;
    }, [models, selectedModelType]);

    const isLoading = isLoadingServices || isLoadingPlans;

    const deselect = useCallback(() => {
        dispatch(setKeyPaymentModel(null));
        dispatch(setNextEnabled(false));
    }, [dispatch]);

    const select = useCallback(
        (model: PaymentModelData) => {
            dispatch(setKeyPaymentModel(model.id));
            dispatch(setNextEnabled(true));
        },
        [dispatch]
    );

    const value = useMemo<KeyHandoverSubscriptionContextValue>(
        () => ({ models, selected, isLoading, select, deselect }),
        [models, selected, isLoading, select, deselect]
    );

    return (
        <KeyHandoverSubscriptionContext.Provider value={value}>
            {children}
        </KeyHandoverSubscriptionContext.Provider>
    );
}

export function useKeyHandoverSubscription() {
    const ctx = useContext(KeyHandoverSubscriptionContext);
    if (!ctx) {
        throw new Error(
            "useKeyHandoverSubscription must be used within KeyHandoverSubscriptionProvider"
        );
    }
    return ctx;
}
